import { useSeo } from "../lib/seo";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-serif text-2xl lg:text-3xl font-medium text-[#0F2A2C] tracking-tight">{title}</h2>
    <div className="mt-3 space-y-3 text-[#4A5568] leading-relaxed">{children}</div>
  </section>
);

const SubHead = ({ children }) => (
  <h3 className="mt-5 font-semibold text-[#0F2A2C] text-lg">{children}</h3>
);

export default function Privacy() {
  useSeo({
    title: "Privacy & Confidentiality | Valora Support Services",
    description: "How Valora Support Services handles personal information in line with the Privacy Act 1988 (Cth) and NDIS requirements.",
  });

  return (
    <div data-testid="privacy-page" className="bg-white">
      <section className="bg-gradient-to-b from-[#E6FFFA] via-[#F8FBFA] to-white">
        <div className="container-x py-16 lg:py-20">
          <p className="eyebrow">Legal</p>
          <h1 className="mt-3 font-serif text-4xl lg:text-5xl font-medium text-[#0F2A2C] tracking-tight">Privacy &amp; Confidentiality</h1>
          <p className="mt-4 max-w-2xl text-[#4A5568]">Last updated: February 2026.</p>
        </div>
      </section>

      <article className="container-x py-14 max-w-3xl">
        <Section title="Introduction">
          <p>
            This policy guarantees that Valora Support Services handles and safeguards personal information in compliance with the NDIS and applicable privacy laws. We respect individuals' privacy rights while understanding that collecting, maintaining, and managing personal information is necessary to ensure a safe working environment and uphold high-quality standards.
          </p>
          <p>
            The information we gather is used to deliver services to participants in a secure and healthy setting that meets their specific needs. It also enables us to fulfil our duty of care responsibilities, make appropriate referrals, and conduct necessary business activities to support the services we provide.
          </p>
        </Section>

        <Section title="Applicability">
          <SubHead>When</SubHead>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Applies to all personal information and sensitive personal information, including the personal information of employees and participants.</li>
            <li>Applies to all company confidential information — that is, any information not publicly available.</li>
          </ul>
          <SubHead>Who</SubHead>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Applies to all representatives, including key management personnel, directors, full-time workers, part-time workers, casual workers, contractors and volunteers.</li>
          </ul>
        </Section>

        <Section title="Regulations relevant to this policy">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>NDIS (Quality Indicators) Guidelines 2018 (Cth)</li>
            <li>NDIS (Provider Registration and Practice Standards) Rules 2018 (Cth)</li>
            <li>Privacy Act 1988 (Cth)</li>
            <li>Privacy Amendment (Notifiable Data Breaches) Act 2017 (Cth)</li>
          </ul>
        </Section>

        <Section title="Privacy and confidentiality guidelines">
          <p>To support the privacy and confidentiality of individuals:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>We are committed to complying with the privacy requirements of the Privacy Act, the Australian Privacy Principles and the Privacy Amendment (Notifiable Data Breaches), as required of organisations providing disability services.</li>
            <li>We are fully committed to complying with the consent requirements of the NDIS Quality and Safeguarding Framework and relevant state or territory requirements.</li>
            <li>We provide all individuals with access to information about the privacy of their personal information.</li>
            <li>Each individual has the right to opt out of consenting to and providing their personal details if they wish.</li>
            <li>Individuals have the right to request access to their personal records by contacting their nominated contact person.</li>
            <li>Where we are required to report to government funding bodies, information provided is non-identifiable and related to services and support hours provided, age, disability, language and nationality.</li>
            <li>Personal information will only be used by Valora and will not be shared outside the organisation without your permission unless required by law (e.g. reporting of assault, abuse, neglect, or where a court order is issued).</li>
            <li>Images or video footage of participants will not be used without their consent.</li>
            <li>Participants have the option of being involved in external NDIS audits if they wish.</li>
          </ul>
        </Section>

        <Section title="Keeping information secure">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>We take reasonable steps to protect the personal information we hold against misuse, interference, loss, unauthorised access, modification and disclosure.</li>
            <li>Personal information is accessible to the participant and is available for use only by relevant workers.</li>
            <li>Security for personal information includes password protection for IT systems, locked filing cabinets and physical access restrictions, with only authorised personnel permitted access.</li>
            <li>Personal information no longer required is securely destroyed or de-identified.</li>
          </ul>
        </Section>

        <Section title="Data breaches">
          <p>As part of our information security responsibilities:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>We will take reasonable steps to reduce the likelihood of a data breach occurring, including storing personal information securely and making it accessible only to relevant workers.</li>
            <li>If we know or suspect your personal information has been accessed by unauthorised parties, and we think this could cause you harm, we will take reasonable steps to reduce the chance of harm and advise you of the breach, and where necessary the Office of the Australian Information Commissioner.</li>
          </ul>
        </Section>

        <Section title="Breach of privacy and confidentiality">
          <p>A breach of privacy and confidentiality is an incident that:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Will be addressed by following our internal incident management process to resolve.</li>
            <li>May require an investigation.</li>
            <li>If intentional, will result in disciplinary action up to and including termination of employment.</li>
          </ul>
        </Section>

        <Section title="Contact us">
          <p>
            Valora Support Services<br />
            13 Appleberry Avenue, Coomera QLD 4209<br />
            Email: <a href="mailto:info@valorasupportservices.com" className="text-[#0B6E70] font-medium underline">info@valorasupportservices.com</a><br />
            Phone: <a href="tel:0426635885" className="text-[#0B6E70] font-medium underline">0426 635 885</a>
          </p>
          <p>See also our <a href="/terms" className="text-[#0B6E70] font-medium underline">Terms and Conditions</a>.</p>
        </Section>
      </article>
    </div>
  );
}
